/**
 * Esiti delle modifiche ▸ Righe — the drill from a decision class to its decided lines (issue 226,
 * giro 3 «Dopo» point 7, `docs/adr/3002` amendment 2026-10-07). The pure half: the address, its
 * parse, the old address it replaces, and the figures the page states. Tested on strings.
 *
 *   `/strategic/override-outcomes/lines?country=…&asOf=…&class=…`
 *
 * The scope is the per-class page's own (`?country=`, `?asOf=`), and `?class=` names the class the
 * row was. No `?class=` is the totals row's drill: every class, as the old picker's «Every class».
 *
 * `?class=` on the per-class page itself was the old pairs picker. That section is this page now,
 * so an old link carrying it is moved here, replacing its history entry ({@link legacyLinesRedirect}).
 */

import { useEffect } from 'react';

import type { OverridePairRow } from '@/api/generated';

import { AS_OF_PARAM, CLASS_PARAM, COUNTRY_PARAM, readIsoDate } from './programme';

export const OVERRIDE_OUTCOMES_PATH = '/strategic/override-outcomes';
export const OVERRIDE_LINES_PATH = '/strategic/override-outcomes/lines';

export interface LinesScope {
  readonly country: string | null;
  /** `null`: the URL names none, and the page reads as at today, as the per-class page does. */
  readonly asOf: string | null;
  /** `null`: every class — the totals row's drill. */
  readonly decisionClass: string | null;
}

const named = (value: string | null): string | null => {
  const trimmed = value?.trim() ?? '';
  return trimmed === '' ? null : trimmed;
};

export function readLinesScope(search: string): LinesScope {
  const params = new URLSearchParams(search);
  return {
    country: named(params.get(COUNTRY_PARAM)),
    asOf: readIsoDate(params.get(AS_OF_PARAM)),
    decisionClass: named(params.get(CLASS_PARAM)),
  };
}

/**
 * `?overridden=true|false` and `?projectionArchived=true|false` — the decided lines narrowed to the ones the override
 * cards count (ADR 9069, platform ADR 6194): the lines the planner overrode (adjusted or rejected) or approved as proposed,
 * and the lines whose recommendation carried a projection or none. They are the platform's own filters on the pairs read
 * (`overridden`, `projection_archived`), applied inside the window and the class, so the list is the card's figure.
 * No parameter is every line.
 */
export const OVERRIDDEN_PARAM = 'overridden';
export const PROJECTION_ARCHIVED_PARAM = 'projectionArchived';

export interface LinesNarrowing {
  /** `true`: only the overridden lines; `false`: only the approved ones; `null`: every decided line. */
  readonly overridden: boolean | null;
  /** `true`: only the lines with an archived projection; `false`: only those with none; `null`: every decided line. */
  readonly projectionArchived: boolean | null;
}

export const NOT_NARROWED: LinesNarrowing = { overridden: null, projectionArchived: null };

const flag = (value: string | null): boolean | null => (value === 'true' ? true : value === 'false' ? false : null);

/** The narrowing an address names; anything but `true` or `false` is no narrowing. Pure. */
export function readLinesNarrowing(search: string): LinesNarrowing {
  const params = new URLSearchParams(search);
  return { overridden: flag(params.get(OVERRIDDEN_PARAM)), projectionArchived: flag(params.get(PROJECTION_ARCHIVED_PARAM)) };
}

/** The narrowing as the pairs read's own parameters — shared by the page and its export, so both list the same lines. */
export function linesNarrowingQuery(narrowing: LinesNarrowing): { readonly overridden?: boolean; readonly projection_archived?: boolean } {
  return {
    ...(narrowing.overridden === null ? {} : { overridden: narrowing.overridden }),
    ...(narrowing.projectionArchived === null ? {} : { projection_archived: narrowing.projectionArchived }),
  };
}

function query(scope: LinesScope, narrowing: LinesNarrowing = NOT_NARROWED): string {
  const params = new URLSearchParams();
  if (scope.country !== null) params.set(COUNTRY_PARAM, scope.country);
  if (scope.asOf !== null) params.set(AS_OF_PARAM, scope.asOf);
  if (scope.decisionClass !== null) params.set(CLASS_PARAM, scope.decisionClass);
  if (narrowing.overridden !== null) params.set(OVERRIDDEN_PARAM, String(narrowing.overridden));
  if (narrowing.projectionArchived !== null) params.set(PROJECTION_ARCHIVED_PARAM, String(narrowing.projectionArchived));
  const text = params.toString();
  return text === '' ? '' : `?${text}`;
}

/** The lines of one class (or of every class, `decisionClass` `null`) under the per-class scope, optionally narrowed. */
export function linesHref(scope: LinesScope, narrowing: LinesNarrowing = NOT_NARROWED): string {
  return `${OVERRIDE_LINES_PATH}${query(scope, narrowing)}`;
}

/** The per-class page under the same market and date — the breadcrumb's way back up. */
export function classesHref(scope: Pick<LinesScope, 'country' | 'asOf'>): string {
  return `${OVERRIDE_OUTCOMES_PATH}${query({ ...scope, decisionClass: null })}`;
}

/**
 * Where an old per-class link that opened the pairs on one class now lives, or `null`. Every other
 * key the link carried travels with it. Pure; `useLegacyLinesRedirect` replaces the entry with it.
 */
export function legacyLinesRedirect(pathname: string, search: string): string | null {
  if (pathname.replace(/\/+$/, '') !== OVERRIDE_OUTCOMES_PATH) return null;
  const params = new URLSearchParams(search);
  if (named(params.get(CLASS_PARAM)) === null) return null;
  return `${OVERRIDE_LINES_PATH}?${params.toString()}`;
}

/** Moves an old per-class link that named a class to its lines, once, on mount — REPLACED, not pushed. */
export function useLegacyLinesRedirect(): void {
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const target = legacyLinesRedirect(window.location.pathname, window.location.search);
    if (target === null) return;
    window.history.replaceState({}, '', target);
    window.dispatchEvent(new PopStateEvent('popstate'));
  }, []);
}

/** What the drill's sentence and figures state — counts over the lines read, nothing modelled. */
export function linesFigures(rows: readonly OverridePairRow[]): {
  readonly lines: number;
  readonly overridden: number;
  readonly withProjection: number;
} {
  return {
    lines: rows.length,
    overridden: rows.filter((row) => row.overridden).length,
    withProjection: rows.filter((row) => row.projectedValue !== null && row.projectedValue !== undefined).length,
  };
}
